import {
    HttpClient,
    HttpHeaders,
} from '@angular/common/http';
import { ODataResponse } from '@models';
import { Mapper } from '@services/generics/data.mapper';
import buildODataQuery, { ODataQueryOptions } from '@services/generics/odata.query';
import { MapperDefinition } from '@services/mapper.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export const ODATA_COUNT_KEY = '@odata.count';

export interface IODataArray<T> {
    value: Array<T>;
    [ODATA_COUNT_KEY]: number;
}

export abstract class ODataService<T, TKey> extends Mapper<T> {
    protected abstract _endpoint: string;
    protected _jsonHeaders?: HttpHeaders;

    constructor(
        protected _http: HttpClient,
        public mapper: MapperDefinition<T>,
    ) {
        super();
        this.mapper = mapper;
    }

    public get(queryOptions?: ODataQueryOptions, endpoint?: string) {
        return this._http.get(`${endpoint || this._endpoint}${this.getQueryString(queryOptions)}`)
            .pipe(
                map((response) => this._mapData(response)),
            ) as Observable<ODataResponse<T>>;
    }

    public getInstance(key: TKey, expand: Array<string> | null = null, params: any = {}) {
        const expansionList = expand === null ? '' : '?$expand=' + expand.join(',');

        return this._http
            .get(`${this._endpoint}(${key})${expansionList}`, {
                params,
            })
            .pipe(
                map((response) => this._mapData(response)),
            ) as Observable<T>;
    }

    public getInstanceSet(key: TKey, queryOptions: ODataQueryOptions) {
        const endpoint = `${this._endpoint}(${key})`;
        return this._http.get(`${endpoint}${this.getQueryString(queryOptions)}`)
            .pipe(
                map((response) => this._mapData(response)),
            ) as Observable<ODataResponse<T>>;
    }

    public add(data: T | Omit<T, 'id'>) {
        const serverData = this._toPoco(data);
        const payload = JSON.stringify(serverData);
        return this._http
            .post(this._endpoint, payload)
            .pipe(
                map((response) => this._mapData(response)),
            ) as Observable<T>;
    }

    public update(key: TKey, data: T): Observable<Object> {
        const serverData = this._toPoco(data);
        const content = JSON.stringify(serverData);

        return this._http
            .put(`${this._endpoint}(${this._keyGen(key)})`, content, {
                headers: this._jsonHeaders,
            });
    }

    public patch<K>(key: TKey, data: K | T): Observable<Object> {
        const serverData = this._toPoco(data);
        const content = JSON.stringify(serverData);

        return this._http.patch(`${this._endpoint}(${this._keyGen(key)})`, content, {
            headers: this._jsonHeaders,
        });
    }

    public delete(key: TKey): Observable<Object> {
        return this._http.delete(`${this._endpoint}(${this._keyGen(key)})`);
    }

    public getQueryString(queryOptions?: ODataQueryOptions): string {
        return !queryOptions ? '' : buildODataQuery(queryOptions);
    }

    protected _mapData = <K>(serverData: any, customMapper?: MapperDefinition<K>): ODataResponse<T | K> | T | K => {
        const items: any[] = serverData.value;

        const mappedData: ODataResponse<T> = {
            data: [],
            total: 0,
        };

        if (items) {
            mappedData.data = items.map(entry => {
                return this._toDto(entry, customMapper);
            });
            mappedData.total = serverData[ODATA_COUNT_KEY];
        }

        if (serverData.Keys && serverData.Values) {
            return this._fromDictionary(serverData);
        }

        if (!items && !serverData.hasOwnProperty(ODATA_COUNT_KEY)) {
            return this._toDto(serverData, customMapper);
        }

        return mappedData;
    }

    private _keyGen(key: TKey): string | TKey {
        if (typeof key === 'string') {
            return `'${key}'`;
        }
        return key;
    }

    private _fromDictionary = (data: any): any => {
        const result = <any>{};
        if (data.Keys && data.Values) {
            for (let i = 0; i < data.Keys.length; i++) {
                result[data.Keys[i]] = data.Values[i];
            }
        }

        return result;
    }
}
